import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import globalNotification from 'live/services/global-notification'
import { AnswerStatusType, npsApi } from '../../api/npsApi'
import i18n from 'live/services/i18n'

type PopupProps = {
    userId: number
}

const scores = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000
const STATUS_CHECK_INTERVAL_MS = 10 * 60 * 1000
const MAX_RETRY_COUNT = 5
const scoreColors = [
    '#d64545',
    '#df5a43',
    '#e56f41',
    '#eb8341',
    '#ef973f',
    '#f1ab3f',
    '#d0b53f',
    '#a7bd44',
    '#7ac45b',
    '#4bbd72',
    '#21a366'
]

const Popup: React.FC<PopupProps> = ({ userId }) => {
    const [isLoading, setIsLoading] = useState(true)
    const [shouldShow, setShouldShow] = useState(false)
    const [existingAnswer, setExistingAnswer] = useState<AnswerStatusType | null>(null)
    const [score, setScore] = useState<number | null>(null)
    const [comment, setComment] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    const canSubmit = useMemo(() => score !== null && !isSubmitting, [score, isSubmitting])

    useEffect(() => {
        let mounted = true
        const checkAnswerStatus = () => {
            if (isSubmitting) {
                return
            }

            npsApi
                .getAnswerStatus(userId)
                .then(response => {
                    if (!mounted) {
                        return
                    }

                    if (!response?.success || !response.data) {
                        setExistingAnswer(null)
                        setShouldShow(false)
                        return
                    }

                    const answer = response.data
                    const retries = answer.numberOfRetries || 0
                    const lastRetryAt = answer.lastRetryAt ? Number(answer.lastRetryAt) : null
                    const canRetryByTime = !lastRetryAt || Date.now() - lastRetryAt >= ONE_DAY_IN_MS
                    const canRetryByCount = retries < MAX_RETRY_COUNT
                    const mustShow = !answer.answered && canRetryByCount && canRetryByTime

                    setExistingAnswer(answer)
                    setShouldShow(mustShow)

                    if (mustShow) {
                        setScore(null)
                        setComment('')
                    }
                })
                .catch(() => {
                    if (!mounted) {
                        return
                    }

                    setExistingAnswer(null)
                    setShouldShow(false)
                })
                .finally(() => {
                    if (mounted) {
                        setIsLoading(false)
                    }
                })
        }

        checkAnswerStatus()
        const intervalId = window.setInterval(checkAnswerStatus, STATUS_CHECK_INTERVAL_MS)

        return () => {
            mounted = false
            window.clearInterval(intervalId)
        }
    }, [userId, isSubmitting])

    const handleSubmit = async () => {
        if (score === null || isSubmitting) {
            return
        }

        setIsSubmitting(true)

        try {
            const response = await npsApi.upsertUserAnswer({
                userId,
                answered: true,
                score,
                comment: comment.trim() || undefined,
                answeredAt: Date.now(),
                numberOfRetries: existingAnswer?.numberOfRetries || 0,
                lastRetryAt: existingAnswer?.lastRetryAt || null
            })

            if (!response?.success) {
                throw new Error(response?.error || 'Failed to submit NPS answer')
            }

            setShouldShow(false)
            globalNotification.success(i18n("Thanks for answering the NPS survey"))
        } catch (error) {
            globalNotification.error(i18n("Could not submit your NPS answer"))
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleClose = async () => {
        if (isSubmitting) {
            return
        }

        const nextRetries = Math.min((existingAnswer?.numberOfRetries || 0) + 1, MAX_RETRY_COUNT)
        const now = Date.now()

        setIsSubmitting(true)

        try {
            const response = await npsApi.upsertUserAnswer({
                userId,
                answered: false,
                score: 0,
                comment: undefined,
                answeredAt: null,
                numberOfRetries: nextRetries,
                lastRetryAt: now
            })

            if (response?.success) {
                setExistingAnswer(response.data || null)
            }
        } catch (error) {
            globalNotification.error(i18n("Could not update retry information"))
        } finally {
            setIsSubmitting(false)
            setShouldShow(false)
        }
    }

    if (isLoading || !shouldShow) {
        return null
    }

    return (
        <Overlay>
            <Card>
                <CloseButton type="button" aria-label={i18n("Close")} onClick={handleClose}>
                    ×
                </CloseButton>

                <Eyebrow>{i18n("NPS Survey")}</Eyebrow>
                <Title>{i18n("How likely are you to recommend this platform to a colleague?")}</Title>
                <Subtitle>{i18n("Choose a score from 0 to 10")}</Subtitle>

                <ScoreGrid>
                    {scores.map(currentScore => (
                        <ScoreButton
                            key={currentScore}
                            type="button"
                            selected={score === currentScore}
                            color={scoreColors[currentScore]}
                            onClick={() => setScore(currentScore)}
                        >
                            {currentScore}
                        </ScoreButton>
                    ))}
                </ScoreGrid>

                <Legend>
                    <span>{i18n("Not likely")}</span>
                    <span>{i18n("Very likely")}</span>
                </Legend>

                <CommentLabel htmlFor="nps-comment">{i18n("Comment (optional)")}</CommentLabel>
                <CommentInput
                    id="nps-comment"
                    value={comment}
                    onChange={event => setComment(event.target.value)}
                    placeholder={i18n("Tell us what influenced your score")}
                    rows={4}
                />

                <SubmitButton type="button" onClick={handleSubmit} disabled={!canSubmit}>
                    {isSubmitting ? i18n("Submitting...") : i18n("Send feedback")}
                </SubmitButton>
            </Card>
        </Overlay>
    )
}

export default Popup

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background:
        radial-gradient(circle at top left, rgba(59, 130, 246, 0.18), transparent 30%),
        radial-gradient(circle at bottom right, rgba(37, 99, 235, 0.24), transparent 35%),
        rgba(17, 24, 39, 0.64);
    backdrop-filter: blur(6px);
`

const Card = styled.div`
    position: relative;
    width: min(100%, 680px);
    padding: 32px;
    border-radius: 24px;
    background: var(--gray-0);
    box-shadow: 0 28px 90px rgba(15, 23, 42, 0.24);
    border: 1px solid rgba(59, 130, 246, 0.16);
`

const CloseButton = styled.button`
    position: absolute;
    top: 16px;
    right: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: 1px solid rgba(59, 130, 246, 0.16);
    border-radius: 999px;
    background: rgba(59, 130, 246, 0.08);
    color: var(--blue-500);
    font-size: 24px;
    line-height: 1;
    cursor: pointer;
`

const Eyebrow = styled.div`
    margin-bottom: 10px;
    color: var(--blue-500);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
`

const Title = styled.h2`
    margin: 0;
    color: var(--gray-900);
    font-size: 28px;
    line-height: 1.2;
`

const Subtitle = styled.p`
    margin: 12px 0 24px;
    color: var(--gray-500);
    font-size: 15px;
    line-height: 1.6;
`

const ScoreGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(11, minmax(0, 1fr));
    gap: 8px;

    @media (max-width: 720px) {
        grid-template-columns: repeat(6, minmax(0, 1fr));
    }
`

const ScoreButton = styled.button<{ selected: boolean; color: string }>`
    min-height: 48px;
    border: 1px solid ${({ selected, color }) => (selected ? color : 'var(--gray-500)')};
    border-radius: 14px;
    background: ${({ selected, color }) => (selected ? `linear-gradient(180deg, ${color} 0%, ${color}dd 100%)` : 'var(--gray-200)')};
    color: ${({ selected }) => (selected ? '#ffffff' : 'var(--gray-900)')};
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease, border-color 0.15s ease;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 10px 18px rgba(15, 23, 42, 0.08);
        border-color: ${({ color }) => color};
    }
`

const Legend = styled.div`
    display: flex;
    justify-content: space-between;
    margin: 12px 0 24px;
    color: var(--gray-500);
    font-size: 12px;
    font-weight: 600;
`

const CommentLabel = styled.label`
    display: block;
    margin-bottom: 8px;
    color: var(--gray-700);
    font-size: 14px;
    font-weight: 600;
`

const CommentInput = styled.textarea`
    width: 100%;
    resize: vertical;
    padding: 14px 16px;
    border: 1px solid var(--gray-300);
    border-radius: 16px;
    background: var(--gray-100);
    color: var(--gray-900);
    font-size: 14px;
    line-height: 1.5;

    &:focus {
        outline: none;
        border-color: var(--blue-500);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
    }
`

const SubmitButton = styled.button`
    width: 100%;
    margin-top: 20px;
    min-height: 52px;
    border: none;
    border-radius: 16px;
    background: linear-gradient(135deg, var(--blue-500) 0%, #2563eb 100%);
    color: var(--white);
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.55;
    }
`
